/**
 * Event bus ; batched, fault-tolerant telemetry pipeline.
 *
 * - Every event lands in MongoDB via /api/events (your data, you own it).
 * - A small whitelist of high-signal events is ALSO mirrored to Meta Pixel
 *   with a deterministic event_id (so the future server-side CAPI event can
 *   deduplicate against the client-side one).
 * - Batches flush every 2s OR when queue ≥ 20 events OR on page-hide via
 *   sendBeacon (so we never lose the last events when the user closes the tab).
 *
 * Identity:
 *   visitor_id  ; persistent UUID in localStorage (lives across sessions)
 *   session_id  ; UUID in sessionStorage (per tab/visit)
 *   route_event_id ; generated on each route change; every event on that route
 *                    carries this id so we can reconstruct sessions cleanly.
 */
import { getAttribution, newEventId, trackEvent, trackCustomEvent, readFbCookies } from "./tracking";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const VISITOR_KEY = "zenith.visitor_id";
const SESSION_KEY = "zenith.session_id";

// ---- Identity ---------------------------------------------------------------
export function getVisitorId() {
  if (typeof window === "undefined") return null;
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = newEventId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}
function getSessionId() {
  if (typeof window === "undefined") return null;
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = newEventId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

let _routeEventId = null;
export function setRouteEventId(id) {
  _routeEventId = id;
}
export function getRouteEventId() {
  return _routeEventId;
}

function pageSource() {
  if (typeof window === "undefined") return "other";
  const p = window.location.pathname;
  if (p === "/" || p === "") return "mortgages";
  if (p.startsWith("/privacy") || p.startsWith("/terms") || p.startsWith("/cookies"))
    return "legal";
  return "other";
}

function clientContext() {
  if (typeof window === "undefined") return {};
  const w = window;
  const vw = w.innerWidth;
  const device = vw < 640 ? "mobile" : vw < 1024 ? "tablet" : "desktop";
  return {
    ua: navigator.userAgent.slice(0, 240),
    viewport: { w: w.innerWidth, h: w.innerHeight },
    dpr: w.devicePixelRatio || 1,
    device,
    lang: navigator.language,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    online: navigator.onLine,
  };
}

// ---- CAPI relay whitelist ---------------------------------------------------
// These Meta standard events are relayed server-side via /api/capi immediately
// after the browser pixel fires - same event_id enables Meta deduplication.
// Purchase is excluded: it goes through the lead-submit flow with full PII.
const CAPI_RELAY = new Set(["PageView", "ViewContent", "InitiateCheckout", "AddToCart"]);
const CAPI_URL = `${BACKEND}/api/capi`;

function _relayCapi(metaEventName, eventId, pageUrl) {
  if (!CAPI_URL || !CAPI_RELAY.has(metaEventName)) return;
  const { fbc, fbp } = readFbCookies();
  const body = JSON.stringify({
    event_name: metaEventName,
    event_id:   eventId,
    page_url:   pageUrl,
    fbc,
    fbp,
    visitor_id: getVisitorId(),   // hashed server-side into Meta external_id (match-quality key)
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : "",
  });
  // sendBeacon survives page unload; fall back to fetch with keepalive
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon(CAPI_URL, blob)) return;
  }
  fetch(CAPI_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => { /* fire-and-forget */ });
}

// ---- Meta Pixel whitelist ---------------------------------------------------
// Names mapped onto Meta's standard event vocabulary. Anything not in this
// map stays purely in our DB. (Layer 5 spec: don't pollute AEM.)
const PIXEL_MAP = {
  page_view:             "PageView",
  view_content:          "ViewContent",
  initiate_checkout:     "InitiateCheckout",
  lead_partial:          "AddToCart",
  // "submit_application" and "lead_submit" intentionally excluded.
  // The Purchase pixel + CAPI event fired in leadSubmit.js is the single
  // conversion signal. Mapping these here caused a duplicate SubmitApplication
  // pixel event on every submit → Meta counted 2 conversions per real lead.
  schedule:              "Schedule",
  contact:               "Contact",
  complete_registration: "CompleteRegistration",
};

// ---- Queue ------------------------------------------------------------------
const MAX_BATCH = 20;
const FLUSH_INTERVAL_MS = 2000;
const _queue = [];        // modified in-place via push/splice - reference never changes
let _flushTimer = null;
let _started = false;

function postBatch(events, opts = {}) {
  const body = JSON.stringify({ events });
  // Periodic (2s) flushes go through fetch: sendBeacon() with an
  // application/json Blob triggers a CORS preflight, and if that ever
  // fails (mis-set CORS_ORIGINS, gateway hiccup) the event is silently
  // dropped with no retry - which is how the admin's Events tab ended up
  // looking empty. fetch surfaces failures and, with keepalive, still
  // survives short unload windows.
  //
  // Only the page-hide / unload path prefers sendBeacon, since keepalive
  // fetches can be cancelled if the tab actually goes away.
  if (opts.unload && typeof navigator !== "undefined" && navigator.sendBeacon && body.length < 60_000) {
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon(`${API}/events`, blob)) return Promise.resolve();
  }
  return fetch(`${API}/events`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {
    /* ignore ; telemetry is fire-and-forget */
  });
}

function scheduleFlush() {
  if (_flushTimer) return;
  _flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
}
function flush(opts = {}) {
  if (_flushTimer) {
    clearTimeout(_flushTimer);
    _flushTimer = null;
  }
  if (_queue.length === 0) return;
  const batch = _queue.splice(0, _queue.length);
  postBatch(batch, opts);
}

/**
 * Track an event.
 * @param {string} name        snake_case event name
 * @param {Object} [props]     event-specific data (NO PII)
 * @param {Object} [opts]
 * @param {boolean} [opts.mirrorToPixel]  force Meta mirror (must be in PIXEL_MAP)
 */
export function track(name, props = {}, opts = {}) {
  if (typeof window === "undefined") return;
  let event_id = newEventId();
  // The first page_view reuses the id of the PageView index.html already fired at parse
  // time, so there is exactly one browser PageView (fired early, with an event_id) and
  // the /api/capi relay below carries the same id for Meta to dedupe against.
  let pvAlreadyFired = false;
  if (name === "page_view" && window.__pvId && !window.__pvUsed) {
    event_id = window.__pvId;
    window.__pvUsed = true;
    pvAlreadyFired = true;
  }
  const ev = {
    event_id,
    name,
    ts: new Date().toISOString(),
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    route_event_id: _routeEventId,
    source: pageSource(),
    page_url: window.location.href,
    page_path: window.location.pathname,
    props,
    client: clientContext(),
    attribution: getAttribution(),
    meta_pixel_fired: false,
    meta_event_id: null,
  };

  const metaName = PIXEL_MAP[name];
  if (metaName && (opts.mirrorToPixel !== false)) {
    if (!pvAlreadyFired) trackEvent(metaName, { _zid: event_id, ...sanitizeForPixel(props) }, event_id);
    ev.meta_pixel_fired = true;
    ev.meta_event_id = event_id;
    // Mirror to server-side CAPI with same event_id → Meta deduplicates to 1 event.
    // Runs after fbq() so the browser event is always logged first.
    _relayCapi(metaName, event_id, ev.page_url);
  }

  _queue.push(ev);
  if (_queue.length >= MAX_BATCH) {
    flush();
  } else {
    scheduleFlush();
  }
  return event_id;
}

/**
 * Fire a Meta CUSTOM event AND record it to the DB bus with the same event_id.
 * For V2 mid-funnel signals (Zen_Bridge1_Fit, Zen_Answer, Zen_Bridge2_Complete,
 * Zen_Exit_*) - high-volume signals to optimize on while Lead volume is thin.
 * Unlike track(), the pixel name is passed through verbatim as a CUSTOM event;
 * it deliberately does NOT consult PIXEL_MAP (which is standard-events-only).
 * No PII in props - same sanitize rule as track().
 */
export function trackCustom(name, props = {}) {
  if (typeof window === "undefined") return;
  const event_id = newEventId();
  const ev = {
    event_id,
    name,
    custom: true,
    ts: new Date().toISOString(),
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    route_event_id: _routeEventId,
    source: pageSource(),
    page_url: window.location.href,
    page_path: window.location.pathname,
    props,
    client: clientContext(),
    attribution: getAttribution(),
    meta_pixel_fired: true,
    meta_event_id: event_id,
  };
  trackCustomEvent(name, sanitizeForPixel(props), event_id);
  _queue.push(ev);
  if (_queue.length >= MAX_BATCH) flush();
  else scheduleFlush();
  return event_id;
}

function sanitizeForPixel(p) {
  // Strip anything starting with _ and any oversized blob.
  const out = {};
  Object.entries(p || {}).forEach(([k, v]) => {
    if (k.startsWith("_")) return;
    if (typeof v === "string" && v.length > 200) return;
    if (typeof v === "object" && v !== null) return;
    out[k] = v;
  });
  return out;
}

// ---- Lifecycle hooks --------------------------------------------------------
export function startEventBus() {
  if (_started || typeof window === "undefined") return;
  _started = true;

  // Flush on page hide / unload via sendBeacon
  const flushOnHide = () => flush({ unload: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushOnHide();
  });
  window.addEventListener("pagehide", flushOnHide);
  window.addEventListener("beforeunload", flushOnHide);
}
